import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Chip,
  Drawer,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined';
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import CallOutlinedIcon from '@mui/icons-material/CallOutlined';
import DnsOutlinedIcon from '@mui/icons-material/DnsOutlined';
import ApiOutlinedIcon from '@mui/icons-material/ApiOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';

const COMING_SOON_IMAGE = 'https://cdn.midjourney.com/6f27c8ca-6356-467a-a2cc-39eab5412810/0_1.png';
const COMING_SOON_VIDEO = 'https://cdn.midjourney.com/video/45f0ed37-deb7-4a4f-9e8e-428e084b5ad0/1.mp4';
const BRAND_BLUE = '#005CC5';

const tagColor = (tag) => {
  const value = String(tag || '').toLowerCase();
  if (value.includes('new')) return 'info';
  if (value.includes('popular')) return 'success';
  if (value.includes('social')) return 'warning';
  if (value.includes('api')) return 'secondary';
  if (value.includes('beta')) return 'error';
  return 'default';
};

const pentestCards = [
  {
    title: 'External Pentest',
    category: 'network',
    route: '/pentest/create/external',
    description: 'External attack surface discovery and exploitation against internet-facing assets.',
    available: true,
    image: 'https://cdn.midjourney.com/87668ff4-21a0-4c9a-8331-897d3e21d1b0/0_1.png',
    video: 'https://cdn.midjourney.com/video/41807f4c-922b-4a37-9e16-b52d790efe07/3.mp4',
    tags: ['Popular', 'Network'],
    compliance: ['SOC 2', 'PCI', 'HIPAA'],
    icon: <LanguageOutlinedIcon fontSize="small" />,
    details: {
      description: 'Simulates a real attacker against internet-facing infrastructure to expose validated business risk.',
      scope: ['Domains', 'IPs', 'CIDRs'],
      methodology: ['Recon and enumeration', 'Service validation and exploit testing', 'Evidence-backed reporting'],
      resources: [{ label: 'Sample Report (External)', url: '#' }],
    },
  },
  {
    title: 'Web Application Pentest',
    category: 'application',
    route: '/pentest/create/web',
    description: 'End-to-end app testing across auth, workflows, business logic, and exposure.',
    available: true,
    image: 'https://cdn.midjourney.com/65e39ab0-7b22-4a0b-9587-af8499360a24/0_1.png',
    video: 'https://cdn.midjourney.com/video/db9f19d0-5f18-4107-a7e5-28d1519047d4/1.mp4',
    tags: ['Popular', 'AppSec'],
    compliance: ['SOC 2', 'PCI', 'HIPAA'],
    icon: <BugReportOutlinedIcon fontSize="small" />,
    details: {
      description: 'Exercises real attack paths through the web experience to uncover exploitable findings before adversaries do.',
      scope: ['URLs', 'Credentials', 'Roles'],
      methodology: ['Application mapping and role review', 'Targeted auth and logic testing', 'Validated findings and remediation'],
      resources: [{ label: 'Sample Report (Web Application)', url: '#' }],
    },
  },
  {
    title: 'API Pentest',
    category: 'application',
    route: null,
    description: 'Specification-driven and exploratory testing of APIs.',
    available: false,
    image: 'https://cdn.midjourney.com/b4913fb2-527d-4f94-9335-089b617ebf6f/0_0.png',
    video: 'https://cdn.midjourney.com/video/6cfdd050-611a-451c-8baa-64f2532221e7/1.mp4',
    tags: ['API'],
    compliance: ['SOC 2', 'PCI', 'HIPAA'],
    icon: <ApiOutlinedIcon fontSize="small" />,
    details: {
      description: 'Focuses on endpoint exposure, authz, data handling, and business-logic abuse for API-first products.',
      scope: ['Domain', 'Credentials', 'OpenAPI or Postman docs'],
      methodology: ['Endpoint mapping and role analysis', 'OWASP API Top 10 testing', 'Evidence, severity, and fixes'],
      resources: [{ label: 'Coming Soon', url: '#' }],
    },
  },
  {
    title: 'Vishing Pentest',
    category: 'social',
    route: '/pentest/create/vishing',
    description: 'Voice phishing simulations to test employee resilience.',
    available: true,
    image: 'https://cdn.midjourney.com/f8a1314a-bdd6-4475-8b8b-7b64297ab91e/0_2.png',
    video: 'https://cdn.midjourney.com/video/c44d317b-3f9c-4153-b24b-bac1b0b7f086/1.mp4',
    tags: ['Social Engineering', 'Phone Calls'],
    compliance: ['SOC 2'],
    icon: <CallOutlinedIcon fontSize="small" />,
    details: {
      description: 'Runs approved voice scenarios to measure handling, escalation, and susceptibility without disrupting operations.',
      scope: ['Phone numbers', 'Scenario', 'Voice profile'],
      methodology: ['Scenario execution', 'Behavior measurement', 'Campaign metrics and training guidance'],
      resources: [{ label: 'Sample Report (Vishing)', url: '#' }],
    },
  },
  {
    title: 'Dark Web Monitoring',
    category: 'intelligence',
    route: '/pentest/create/darkweb',
    description: 'Continuous monitoring for leaked creds, PII, and brand mentions.',
    available: true,
    image: 'https://cdn.midjourney.com/65e39ab0-7b22-4a0b-9587-af8499360a24/0_3.png',
    video: 'https://cdn.midjourney.com/video/d6d3e012-9b8f-4075-8744-d35a28c639f4/0.mp4',
    tags: ['Threat Intel'],
    compliance: ['SOC 2', 'HIPAA'],
    icon: <ShieldOutlinedIcon fontSize="small" />,
    details: {
      description: 'Monitors breach dumps, forums, and dark-web sources to surface exposure before it is weaponized.',
      scope: ['Domain'],
      methodology: ['Collection and detection', 'Exposure triage', 'Snapshot reporting and guidance'],
      resources: [{ label: 'Sample Findings Snapshot', url: '#' }],
    },
  },
  {
    title: 'Internal Pentest',
    category: 'network',
    route: null,
    description: 'On-network adversary simulation across enterprise infrastructure.',
    available: false,
    image: COMING_SOON_IMAGE,
    video: COMING_SOON_VIDEO,
    tags: ['Network', 'Enterprise'],
    compliance: ['SOC 2', 'HIPAA'],
    icon: <DnsOutlinedIcon fontSize="small" />,
    details: { description: 'Assesses directory abuse, lateral movement, and privilege escalation inside the network.', scope: ['TBD'], methodology: ['TBD'], resources: [{ label: 'Coming Soon', url: '#' }] },
  },
  {
    title: 'OSINT Scan',
    category: 'intelligence',
    route: '/pentest/create/osint',
    description: 'Open-source intelligence gathering to expose visible attack paths.',
    available: true,
    image: COMING_SOON_IMAGE,
    video: COMING_SOON_VIDEO,
    tags: ['New', 'Recon', 'OSINT'],
    compliance: ['SOC 2'],
    icon: <SearchIcon fontSize="small" />,
    details: {
      description: 'Surfaces public exposure and open-source signals that inform attacker pathing before hands-on testing begins.',
      scope: ['Single domain or URL'],
      methodology: ['Public recon and enrichment', 'Exposure signal collection', 'OSINT summary and next-step guidance'],
      resources: [{ label: 'Launch OSINT Scan', url: '#' }],
    },
  },
  {
    title: 'SMS Phishing Pentest',
    category: 'social',
    route: null,
    description: 'Text message phishing simulations across SMS and messaging channels.',
    available: false,
    image: COMING_SOON_IMAGE,
    video: COMING_SOON_VIDEO,
    tags: ['SMS', 'WhatsApp', 'Social Engineering'],
    compliance: ['SOC 2', 'HIPAA'],
    icon: <DnsOutlinedIcon fontSize="small" />,
    details: { description: 'Measures response to realistic message-driven phishing scenarios.', scope: ['TBD'], methodology: ['TBD'], resources: [{ label: 'Coming Soon', url: '#' }] },
  },
  {
    title: 'Phishing Pentest',
    category: 'social',
    route: null,
    description: 'Email phishing simulations to test awareness and handling behavior.',
    available: false,
    image: COMING_SOON_IMAGE,
    video: COMING_SOON_VIDEO,
    tags: ['Email', 'Social Engineering'],
    compliance: ['SOC 2', 'HIPAA'],
    icon: <DnsOutlinedIcon fontSize="small" />,
    details: { description: 'Assesses employee response to phishing content, links, and escalation cues.', scope: ['TBD'], methodology: ['TBD'], resources: [{ label: 'Coming Soon', url: '#' }] },
  },
];

const CATEGORY_ORDER = ['social', 'network', 'application', 'intelligence'];
const CATEGORY_META = {
  application: { label: 'Application Security', description: 'Web and API interfaces tested for auth, logic, and data exposure.', icon: BugReportOutlinedIcon, accent: '#2563eb' },
  network: { label: 'Network and Infrastructure', description: 'Perimeter and internal attack surfaces assessed like a real adversary.', icon: LanguageOutlinedIcon, accent: '#0f766e' },
  social: { label: 'Social Engineering', description: 'Voice, email, and messaging simulations centered on human risk.', icon: CallOutlinedIcon, accent: '#ea580c' },
  intelligence: { label: 'Threat Exposure', description: 'Exposure monitoring, dark web visibility, and recon-oriented work.', icon: ShieldOutlinedIcon, accent: '#7c3aed' },
};

const sortCards = (cards) => [...cards].sort((a, b) => (a.available === b.available ? a.title.localeCompare(b.title) : a.available ? -1 : 1));

const OverviewStat = ({ value, label, helper }) => (
  <Box
    sx={{
      p: 1.4,
      borderRadius: 3,
      border: `1px solid ${alpha(BRAND_BLUE, 0.14)}`,
      bgcolor: alpha(BRAND_BLUE, 0.05),
      boxShadow: `0 10px 24px ${alpha(BRAND_BLUE, 0.08)}`,
    }}
  >
    <Typography sx={{ fontSize: '1.45rem', fontWeight: 950, lineHeight: 1.05, color: '#0f172a' }}>{value}</Typography>
    <Typography sx={{ mt: 0.2, fontSize: '0.82rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: BRAND_BLUE }}>{label}</Typography>
    <Typography sx={{ mt: 0.35, fontSize: '0.76rem', lineHeight: 1.45, color: 'rgba(15,23,42,0.72)' }}>{helper}</Typography>
  </Box>
);

const Section = ({ icon, title, children }) => (
  <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, borderColor: 'divider', bgcolor: 'background.paper' }}>
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
      <Box sx={{ display: 'grid', placeItems: 'center' }}>{icon}</Box>
      <Typography variant="overline" sx={{ letterSpacing: 0.6 }}>{title}</Typography>
    </Stack>
    {children}
  </Paper>
);

const CompactPentestCard = ({ card, category, hoveredCard, setHoveredCard, openDrawer, handleSelect }) => {
  const CategoryIcon = category.icon;
  const isBeta = (card.tags || []).some((tag) => String(tag).toLowerCase() === 'beta');
  const visibleTags = isBeta
    ? ['Beta', ...(card.tags || []).filter((tag) => String(tag).toLowerCase() !== 'beta')].slice(0, 3)
    : (card.tags || []).slice(0, 2);
  const availableStyles = card.available
    ? { bgcolor: 'rgba(255,255,255,0.90)', color: '#0f172a', border: '1px solid rgba(255,255,255,0.95)' }
    : { bgcolor: 'rgba(15,23,42,0.72)', color: '#ffffff', border: '1px solid rgba(255,255,255,0.22)' };

  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={() => openDrawer(card)}
      onKeyDown={(event) => { if (event.key === 'Enter') openDrawer(card); }}
      elevation={0}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 4,
        overflow: 'hidden',
        border: `1px solid ${alpha(category.accent, card.available ? 0.18 : 0.12)}`,
        boxShadow: card.available ? `0 16px 32px ${alpha(category.accent, 0.10)}` : '0 10px 22px rgba(15, 23, 42, 0.08)',
        background: 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)',
        transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 20px 38px ${alpha(category.accent, 0.14)}`,
          borderColor: alpha(category.accent, 0.28),
        },
      }}
    >
      <Box
        sx={{ position: 'relative' }}
        onMouseEnter={() => setHoveredCard(card.title)}
        onMouseLeave={() => setHoveredCard((prev) => (prev === card.title ? null : prev))}
      >
        {hoveredCard === card.title ? (
          <CardMedia component="video" height="152" src={card.video} poster={card.image} preload="none" muted autoPlay loop playsInline sx={{ objectFit: 'cover' }} />
        ) : (
          <CardMedia component="img" height="152" src={card.image} alt={card.title} sx={{ objectFit: 'cover' }} />
        )}

        <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(9,17,31,0.12) 0%, rgba(9,17,31,0.76) 100%)' }} />

        <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ position: 'absolute', top: 12, left: 12, right: 12 }}>
          <Chip
            size="small"
            icon={<CategoryIcon fontSize="small" />}
            label={category.label}
            sx={{
              maxWidth: 176,
              bgcolor: 'rgba(15,23,42,0.52)',
              color: '#ffffff',
              border: '1px solid rgba(255,255,255,0.18)',
              backdropFilter: 'blur(10px)',
              '& .MuiChip-icon': { color: '#ffffff' },
              '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
            }}
          />
          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" justifyContent="flex-end">
            {isBeta ? (
              <Chip
                size="small"
                label="Beta"
                color="error"
                sx={{
                  color: '#ffffff',
                  fontWeight: 800,
                  border: '1px solid rgba(255,255,255,0.22)',
                  backdropFilter: 'blur(10px)',
                }}
              />
            ) : null}
            <Chip size="small" label={card.available ? 'Available now' : 'Coming soon'} sx={{ ...availableStyles, fontWeight: 800, backdropFilter: 'blur(10px)' }} />
          </Stack>
        </Stack>

        <Box sx={{ position: 'absolute', left: 14, right: 14, bottom: 14, color: '#ffffff' }}>
          <Typography sx={{ fontSize: '1rem', fontWeight: 900, lineHeight: 1.2 }}>{card.title}</Typography>
          <Typography sx={{ mt: 0.55, fontSize: '0.78rem', lineHeight: 1.45, color: 'rgba(255,255,255,0.86)' }}>
            {(card.compliance || []).length ? card.compliance.join(' | ') : 'Mission brief, scope, and delivery details inside'}
          </Typography>
        </Box>
      </Box>

      <CardContent sx={{ flexGrow: 1, px: 1.7, py: 1.55, pb: 1.2 }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ lineHeight: 1.6, minHeight: 44, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
        >
          {card.description}
        </Typography>

        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1.2 }}>
          {visibleTags.map((tag) => (
            <Chip key={tag} label={tag} size="small" variant="outlined" sx={{ borderColor: alpha(category.accent, 0.22), bgcolor: alpha(category.accent, 0.04), color: alpha('#0f172a', 0.82) }} />
          ))}
          {(card.compliance || []).length > 0 ? (
            <Chip size="small" label={`${card.compliance.length} framework${card.compliance.length > 1 ? 's' : ''}`} variant="outlined" sx={{ borderColor: 'rgba(15,23,42,0.12)', color: 'text.secondary' }} />
          ) : null}
        </Stack>
      </CardContent>

      <CardActions sx={{ px: 1.7, pb: 1.7, pt: 0, gap: 1 }}>
        {card.available ? (
          <Button
            size="small"
            variant="contained"
            onClick={(event) => { event.stopPropagation(); handleSelect(card); }}
            sx={{ flex: 1, fontWeight: 900, borderRadius: 2.5, bgcolor: category.accent, '&:hover': { bgcolor: category.accent } }}
          >
            Start
          </Button>
        ) : (
          <Tooltip title="This option is not yet available">
            <span style={{ display: 'flex', flex: 1 }}>
              <Button size="small" variant="contained" disabled fullWidth sx={{ fontWeight: 900, borderRadius: 2.5 }}>
                Coming Soon
              </Button>
            </span>
          </Tooltip>
        )}

        <Button
          size="small"
          variant="outlined"
          startIcon={<InfoOutlinedIcon />}
          onClick={(event) => { event.stopPropagation(); openDrawer(card); }}
          sx={{
            borderRadius: 2.5,
            borderColor: alpha(category.accent, 0.24),
            color: category.accent,
            '&:hover': { borderColor: alpha(category.accent, 0.38), backgroundColor: alpha(category.accent, 0.05) },
          }}
        >
          Details
        </Button>
      </CardActions>
    </Card>
  );
};

const MissionDrawer = ({ open, onClose, card, onStart }) => {
  if (!card) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 560 }, maxWidth: '100vw', bgcolor: 'background.default', display: 'flex', flexDirection: 'column', overflow: 'hidden' } }}
    >
      <Box sx={{ px: 2, py: 1.5, bgcolor: 'primary.main', color: 'common.white', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Stack spacing={0}>
          <Typography sx={{ fontWeight: 950, letterSpacing: 0.2 }}>{card.title}</Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>Mission brief | scope | methodology | deliverables</Typography>
        </Stack>
        <IconButton onClick={onClose} sx={{ color: 'common.white' }}><CloseIcon /></IconButton>
      </Box>

      <Box sx={{ p: 2, overflowY: 'auto', flex: '1 1 auto', pb: 2 }}>
        <Box sx={{ position: 'relative', borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Box sx={{ position: 'relative', height: 210 }}>
            <Box sx={{ position: 'absolute', inset: 0, backgroundImage: `url(${card.image})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
            <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0.08), rgba(0,0,0,0.72))' }} />
            <Box sx={{ position: 'absolute', left: 16, right: 16, bottom: 14, color: 'common.white' }}>
              <Stack spacing={1}>
                <Typography variant="h6" sx={{ fontWeight: 950, display: 'flex', alignItems: 'center', gap: 1 }}>{card.icon} {card.title}</Typography>
                <Typography variant="body2" sx={{ opacity: 0.92, lineHeight: 1.6 }}>{card.description}</Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {(card.compliance || []).map((item) => (
                    <Chip key={item} size="small" label={item} sx={{ bgcolor: 'rgba(255,255,255,0.16)', color: '#ffffff', border: '1px solid rgba(255,255,255,0.28)', backdropFilter: 'blur(6px)' }} />
                  ))}
                  {(card.tags || []).map((tag) => (
                    <Chip key={tag} size="small" label={tag} color={tagColor(tag)} sx={{ color: '#ffffff' }} />
                  ))}
                  {!card.available ? <Chip icon={<LockOutlinedIcon />} size="small" label="Coming soon" sx={{ bgcolor: 'rgba(255,255,255,0.90)' }} /> : null}
                </Stack>
              </Stack>
            </Box>
          </Box>
        </Box>

        <Box sx={{ height: 14 }} />
        <Section icon={<ArticleOutlinedIcon fontSize="small" color="primary" />} title="Description">
          <Typography variant="body2" sx={{ lineHeight: 1.85 }}>{card.details?.description}</Typography>
        </Section>
        <Box sx={{ height: 12 }} />
        <Section icon={<ChecklistOutlinedIcon fontSize="small" color="primary" />} title="Scope Inputs">
          <List dense sx={{ py: 0 }}>
            {(card.details?.scope || []).map((item, index) => (
              <ListItem key={index} disableGutters sx={{ py: 0.35 }}>
                <ListItemIcon sx={{ minWidth: 32 }}><CheckCircleOutlineIcon fontSize="small" color="success" /></ListItemIcon>
                <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={item} />
              </ListItem>
            ))}
          </List>
        </Section>
        <Box sx={{ height: 12 }} />
        <Section icon={<RouteOutlinedIcon fontSize="small" color="primary" />} title="Methodology">
          <Stepper orientation="vertical" activeStep={-1} sx={{ mt: 0.5 }}>
            {(card.details?.methodology || []).map((step, index) => (
              <Step key={index}>
                <StepLabel><Typography variant="body2" sx={{ lineHeight: 1.6 }}>{step}</Typography></StepLabel>
              </Step>
            ))}
          </Stepper>
        </Section>
        <Box sx={{ height: 12 }} />
        <Section icon={<LinkOutlinedIcon fontSize="small" color="primary" />} title="Resources">
          <Stack direction="column" spacing={1}>
            {(card.details?.resources || []).map((resource, index) => (
              <Button key={index} component="a" href={resource.url} target="_blank" rel="noopener noreferrer" variant="outlined" size="small" startIcon={<LinkOutlinedIcon />} sx={{ alignSelf: 'flex-start', borderRadius: 2.5 }}>
                {resource.label}
              </Button>
            ))}
          </Stack>
        </Section>
      </Box>

      <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Stack direction="row" spacing={1}>
          <Button variant="contained" disabled={!card.available} onClick={onStart} sx={{ borderRadius: 3, py: 1.2, fontWeight: 950, flex: 1, minWidth: 0 }}>
            Start {card.title}
          </Button>
          <Button variant="outlined" onClick={onClose} sx={{ borderRadius: 3, flex: '0 0 auto' }}>Close</Button>
        </Stack>
      </Box>
    </Drawer>
  );
};

const CreatePentest = () => {
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeCard, setActiveCard] = useState(null);
  const [query, setQuery] = useState('');
  const [complianceFilter, setComplianceFilter] = useState('All');

  useEffect(() => {
    const onKeyDown = (event) => {
      const isK = (event.key || '').toLowerCase() === 'k';
      if ((event.metaKey || event.ctrlKey) && isK) {
        event.preventDefault();
        searchRef.current?.focus?.();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const complianceOptions = useMemo(() => ['All', ...Array.from(new Set(pentestCards.flatMap((card) => card.compliance || [])))], []);
  const filteredCards = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return pentestCards.filter((card) => {
      if (complianceFilter !== 'All' && !(card.compliance || []).includes(complianceFilter)) return false;
      if (!normalized) return true;
      const haystack = [card.title, card.description, CATEGORY_META[card.category]?.label, ...(card.tags || []), ...(card.compliance || [])].join(' ').toLowerCase();
      return haystack.includes(normalized);
    });
  }, [query, complianceFilter]);

  const groupedCards = useMemo(() => CATEGORY_ORDER.map((key) => ({
    key,
    meta: CATEGORY_META[key],
    cards: sortCards(filteredCards.filter((card) => card.category === key)),
  })).filter((section) => section.cards.length > 0), [filteredCards]);

  const availableCount = filteredCards.filter((card) => card.available).length;
  const comingSoonCount = filteredCards.length - availableCount;
  const handleSelect = (card) => { if (card?.available && card.route) navigate(card.route); };
  const openDrawer = (card) => { setActiveCard(card); setDrawerOpen(true); };

  return (
    <Box sx={{ flexGrow: 1, p: { xs: 2, md: 3 }, pb: 6 }}>
      <Paper
        elevation={0}
        sx={{
          position: 'relative',
          borderRadius: 5,
          overflow: 'hidden',
          border: `1px solid ${alpha(BRAND_BLUE, 0.16)}`,
          mb: 2.25,
          background: 'linear-gradient(180deg, #ffffff 0%, #f6faff 100%)',
          color: '#0f172a',
          boxShadow: `0 18px 38px ${alpha(BRAND_BLUE, 0.08)}`,
        }}
      >
        <Box aria-hidden sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 6, background: BRAND_BLUE }} />
        <Box aria-hidden sx={{ position: 'absolute', top: -80, right: -40, width: 280, height: 280, borderRadius: 8, background: `radial-gradient(circle, ${alpha(BRAND_BLUE, 0.14)} 0%, rgba(255,255,255,0) 72%)`, transform: 'rotate(18deg)' }} />
        <Box sx={{ position: 'relative', px: { xs: 2, md: 3 }, py: { xs: 2.4, md: 3 } }}>
          <Grid container spacing={2.25} alignItems="stretch">
            <Grid item xs={12} lg={7}>
              <Stack spacing={1.2} sx={{ maxWidth: 700 }}>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase', color: BRAND_BLUE }}>AI Agents Catalog</Typography>
                <Typography variant="h3" sx={{ fontWeight: 950, letterSpacing: '-0.03em', lineHeight: 1.04, fontSize: { xs: '2rem', md: '2.7rem' } }}>
                  Launch a Pentest
                </Typography>
                <Typography sx={{ maxWidth: 620, fontSize: '1rem', lineHeight: 1.7, color: 'rgba(15,23,42,0.72)' }}>
                  Choose the assessment that matches the target, scope, and testing style.
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {CATEGORY_ORDER.map((key) => {
                    const meta = CATEGORY_META[key];
                    const CategoryIcon = meta.icon;
                    return (
                      <Chip
                        key={key}
                        icon={<CategoryIcon fontSize="small" />}
                        label={meta.label}
                        sx={{
                          bgcolor: alpha(BRAND_BLUE, 0.06),
                          color: BRAND_BLUE,
                          border: `1px solid ${alpha(BRAND_BLUE, 0.14)}`,
                          '& .MuiChip-icon': { color: BRAND_BLUE },
                        }}
                      />
                    );
                  })}
                </Stack>
              </Stack>
            </Grid>
            <Grid item xs={12} lg={5}>
              <Grid container spacing={1.2}>
                <Grid item xs={12} sm={4} lg={12} xl={4}><OverviewStat value={availableCount} label="Available Now" helper="Assessments you can launch immediately." /></Grid>
                <Grid item xs={12} sm={4} lg={12} xl={4}><OverviewStat value={comingSoonCount} label="Coming Soon" helper="Future tests already mapped into the catalog." /></Grid>
                <Grid item xs={12} sm={4} lg={12} xl={4}><OverviewStat value={groupedCards.length} label="Categories" helper="Grouped by buying motion, not just feature name." /></Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      <Paper elevation={0} sx={{ mb: 2.4, p: { xs: 1.6, md: 2 }, borderRadius: 4, border: '1px solid rgba(15,23,42,0.08)', background: 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)' }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} lg={6.5}>
            <TextField
              inputRef={searchRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              fullWidth
              placeholder='Search by test, tag, framework, or delivery style (Ctrl+K)'
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: '#ffffff' } }}
            />
          </Grid>
          <Grid item xs={12} lg={5.5}>
            <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', lg: 'flex-end' }} useFlexGap flexWrap="wrap">
              <ToggleButtonGroup value={complianceFilter} exclusive size="small" onChange={(_, value) => value && setComplianceFilter(value)} sx={{ borderRadius: 3, '& .MuiToggleButton-root': { borderRadius: 3, px: 1.25, textTransform: 'none', fontWeight: 700 } }}>
                {complianceOptions.map((option) => <ToggleButton key={option} value={option}>{option}</ToggleButton>)}
              </ToggleButtonGroup>
              {(query || complianceFilter !== 'All') ? <Button size="small" variant="text" onClick={() => { setQuery(''); setComplianceFilter('All'); }} sx={{ fontWeight: 800 }}>Clear filters</Button> : null}
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <Stack spacing={2.4}>
        {groupedCards.map((section) => {
          const CategoryIcon = section.meta.icon;
          const liveCount = section.cards.filter((card) => card.available).length;
          const plannedCount = section.cards.length - liveCount;
          return (
            <Paper key={section.key} elevation={0} sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 4, border: `1px solid ${alpha(section.meta.accent, 0.15)}`, background: `linear-gradient(135deg, ${alpha(section.meta.accent, 0.10)} 0%, rgba(255,255,255,0.97) 100%)` }}>
              <Stack spacing={1.6}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                  <Stack direction="row" spacing={1.2} alignItems="flex-start">
                    <Avatar sx={{ width: 40, height: 40, bgcolor: alpha(section.meta.accent, 0.12), color: section.meta.accent, border: `1px solid ${alpha(section.meta.accent, 0.18)}` }}><CategoryIcon fontSize="small" /></Avatar>
                    <Box>
                      <Typography sx={{ fontSize: '1.15rem', fontWeight: 900, color: '#0f172a' }}>{section.meta.label}</Typography>
                      <Typography sx={{ mt: 0.25, maxWidth: 760, fontSize: '0.92rem', lineHeight: 1.6, color: 'rgba(15,23,42,0.70)' }}>{section.meta.description}</Typography>
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={0.85} useFlexGap flexWrap="wrap">
                    <Chip size="small" label={`${section.cards.length} option${section.cards.length > 1 ? 's' : ''}`} sx={{ bgcolor: '#ffffff', border: `1px solid ${alpha(section.meta.accent, 0.14)}`, fontWeight: 700 }} />
                    <Chip size="small" label={`${liveCount} live`} sx={{ bgcolor: alpha(section.meta.accent, 0.08), color: section.meta.accent, border: `1px solid ${alpha(section.meta.accent, 0.16)}`, fontWeight: 800 }} />
                    {plannedCount > 0 ? <Chip size="small" label={`${plannedCount} planned`} sx={{ bgcolor: '#ffffff', border: '1px solid rgba(15,23,42,0.10)', color: 'rgba(15,23,42,0.72)', fontWeight: 700 }} /> : null}
                  </Stack>
                </Stack>

                <Grid container spacing={2}>
                  {section.cards.map((card) => (
                    <Grid item xs={12} sm={6} lg={4} xl={3} key={card.title}>
                      <CompactPentestCard card={card} category={section.meta} hoveredCard={hoveredCard} setHoveredCard={setHoveredCard} openDrawer={openDrawer} handleSelect={handleSelect} />
                    </Grid>
                  ))}
                </Grid>
              </Stack>
            </Paper>
          );
        })}
      </Stack>

      {!filteredCards.length ? (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, mt: 2 }}>
          <Typography sx={{ fontWeight: 900 }}>No matches</Typography>
          <Typography variant="body2" color="text.secondary">Try clearing filters or searching for a broader term like "SOC 2", "API", or "external".</Typography>
        </Paper>
      ) : null}

      <MissionDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        card={activeCard}
        onStart={() => {
          setDrawerOpen(false);
          if (activeCard) handleSelect(activeCard);
        }}
      />
    </Box>
  );
};

export default CreatePentest;
